import React, { type JSX } from 'react';
import { Create } from '@mui/icons-material';
import CustomIcon from '../../components/CustomIcon';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { useDebounce, useRBAC } from '../../hooks';
import { useGetDeviceByIdQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import {
    Container,
    IconButton,
    List,
    Stack,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Skeleton,
    Button,
} from '@mui/material';
import { ChannelValue, InfoListItem } from '@brightlayer-ui/react-components';
import EditNotifications from '@mui/icons-material/EditNotifications';
import InfoIcon from '@mui/icons-material/Info';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import { useTheme } from '@mui/material/styles';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { NotificationLog } from '@brightlayer-ui/icons-mui';
import { ListSkeleton } from 'pages/WidgetManagement/common/Skeletons';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { ThresholdModal } from './ThresholdModal';
import { useCustomDetailsApi, useHandleDeviceDetails } from './hooks';
import { CustomEmptyState } from 'components/CustomEmptyState';
import { CustomTransComponent } from 'components';
import { CustomModal, ModalActions } from 'components/CustomModal';
import { useTransformData } from 'hooks/useTransformData';
import { InputWithCustomPlaceholder } from 'components/InputWithCustomPlaceholder';
import { subscribeTopic, unsubscribeTopic } from '@fiji/common/src/mqtt_connection/mqttConnection';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { useGetUserProfileQuery } from '@fiji/common/src/features/profile/profileApi';
import { useGetAllFeatureFlagQuery } from '@fiji/common/src/features/featureFlag/featureFlagApi';
import { useIconLibraryFlag } from '@fiji/common/src/hooks';
import { FeatureFlagData, UserProfile } from '@fiji/common/src/types';
import { ApiEndpointType, ApiResponseType } from '@fiji/common/src/features/common/commonTypes';
import {
    setMessageContent,
    setAppHeaderSubTitle,
    setAppHeaderTitle,
} from '@fiji/common/src/features/common/commonSlice';
import { useTranslation } from 'react-i18next';

/**
 * Determines the appropriate icon for a channel based on its icon configuration
 */
const getChannelIconComponent = (channel: any, isIconLibraryEnabled: boolean): JSX.Element => {
    const { icon = {} } = channel ?? {};
    const { URL, name, isExternal, web = {}, iconLibrary = {} } = icon;
    const { family: webFamily = '', name: webIconName = '' } = web;
    const { name: iconLibraryName } = iconLibrary;

    // No URL or name, use web family/name
    if (!URL && !name) {
        return <CustomIcon family={webFamily} iconName={webIconName} />;
    }

    // External icon URL
    if (isExternal === true) {
        return <img src={URL} height={22} width={22} />;
    }

    // Icon library enabled with name and iconLibrary
    if (isIconLibraryEnabled && name && iconLibraryName) {
        return <CustomIcon family={iconLibraryName} iconName={name} />;
    }

    // Fallback to custom icon with name only
    return <CustomIcon family={''} iconName={name} />;
};

const renderLoadingAccordion = (theme: any): JSX.Element => (
    <Accordion defaultExpanded>
        <AccordionSummary
            expandIcon={<ExpandMore color="primary" />}
            aria-controls="panel1bh-content"
            id="expand"
            className="border-bottom-1"
        >
            <Skeleton animation="wave">
                <Typography
                    variant="body2"
                    fontWeight={'600'}
                    fontSize={'14px'}
                    sx={{ color: theme.palette?.primary?.main }}
                >
                    <CustomTransComponent translationKey={'COMMON:PRIORITY'} />
                </Typography>
            </Skeleton>
        </AccordionSummary>
        <AccordionDetails className="padding0">
            <ListSkeleton total={10} customSize={25} customSx={{ height: '4.5rem' }} />
        </AccordionDetails>
    </Accordion>
);

const canSubscribeToDeviceTopic = (mqttConnectionStatus: boolean, profileData: any, selectedNode: any): boolean =>
    mqttConnectionStatus === true && Boolean(profileData?.data) && Boolean(selectedNode?.groupId);

const getDeviceTopic = (profileData: any, currentOrg: any, selectedNode: any, deviceId: string | undefined): string => {
    const realmId = profileData?.data?.currentRealmId;
    const organizationId = currentOrg?.id;
    const groupId = selectedNode?.groupId;
    return `BSSRM/TREND/${realmId}/${organizationId}/${groupId}/${deviceId}/devices`;
};

type DeviceDetailsHeaderProps = {
    theme: any;
    searchKey: string | undefined;
    featureflagValue: FeatureFlagData;
    expandedCategories: any;
    searchPlaceholder: string;
    collapseLabel: JSX.Element;
    expandLabel: JSX.Element;
    onSearch: (value: string) => void;
    onToggleExpand: () => void;
    onEditChannel: () => void;
    manageChannelCategories: boolean;
};

const DeviceDetailsHeader = ({
    theme,
    searchKey,
    featureflagValue,
    expandedCategories,
    searchPlaceholder,
    collapseLabel,
    expandLabel,
    onSearch,
    onToggleExpand,
    onEditChannel,
    manageChannelCategories,
}: DeviceDetailsHeaderProps): JSX.Element => (
    <Stack mb={2} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Stack direction={'row'} alignItems={'center'} spacing={3}>
            <InputWithCustomPlaceholder
                id="searchKey"
                value={searchKey ?? ''}
                placeholder={searchPlaceholder}
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void => onSearch(event.target.value)}
            />
        </Stack>
        <Stack direction={'row'} spacing={2} alignItems={'center'}>
            <Button
                id="toggleExpand"
                data-testid="toggle-expand-btn"
                sx={{
                    border: `1px solid ${theme?.palette?.primary?.main}`,
                    '&:hover': { backgroundColor: theme?.palette?.primary?.[50] },
                }}
                variant="outlined"
                onClick={onToggleExpand}
                startIcon={<UnfoldMore />}
            >
                {Object.values(expandedCategories).every((item: any) => item === true) ? collapseLabel : expandLabel}
            </Button>
            {featureflagValue.enabled && manageChannelCategories && (
                <IconButton
                    id="edit-channel"
                    data-testid="edit-channel-btn"
                    aria-label="settings"
                    onClick={onEditChannel}
                >
                    <Create className="color-content" />
                </IconButton>
            )}
        </Stack>
    </Stack>
);

const resolveChannelValue = (channel: any, pointConversion: (value: any) => any): any => {
    if (channel?.enumDisplayName) return channel.enumDisplayName;
    if (channel?.enumName) return channel.enumName;
    return channel?.value !== 'true' && channel?.value !== 'false' ? pointConversion(channel?.value) : channel?.value;
};

const SingleChannelDetails = ({
    channel,
    channelValue,
    canShowInfoIcon,
    canShowThresholdIcon,
    canUpdateThreshold,
    selectedNode,
    handleInfoIconClick,
    thresholdModalRef,
    t,
}: any): JSX.Element => (
    <Stack flexDirection={'row'} justifyContent={'space-between'}>
        {canShowInfoIcon(channelValue) ? (
            <IconButton
                id="info"
                onClick={(): void =>
                    handleInfoIconClick(channel?.displayName ?? t('COMMON:DETAILS'), channelValue as string)
                }
            >
                <InfoIcon />
            </IconButton>
        ) : (
            <ChannelValue unitSpace="show" value={channelValue} units={channel?.unit} />
        )}
        <Stack flexDirection={'row'}>
            {canShowThresholdIcon(channel) && (
                <IconButton
                    id="threshold"
                    data-testid="threshold-btn"
                    disabled={selectedNode?.connectionStatus === 'Offline'}
                    onClick={(): void => thresholdModalRef?.current?.onClick(channel)}
                >
                    {canUpdateThreshold ? <EditNotifications /> : <NotificationLog />}
                </IconButton>
            )}
        </Stack>
    </Stack>
);

const ChannelRow = ({
    channel,
    getIcon,
    pointConversion,
    shouldShowInfoIcon,
    canShowThresholdIcon,
    canUpdateThreshold,
    selectedNode,
    handleInfoIconClick,
    thresholdModalRef,
    t,
}: any): JSX.Element => {
    const channelValue = resolveChannelValue(channel, pointConversion);
    return (
        <InfoListItem
            key={channel?.channelId}
            icon={getIcon(channel)}
            divider="full"
            title={channel?.displayName}
            rightComponent={
                <SingleChannelDetails
                    channel={channel}
                    channelValue={channelValue}
                    canShowInfoIcon={shouldShowInfoIcon}
                    canShowThresholdIcon={canShowThresholdIcon}
                    canUpdateThreshold={canUpdateThreshold}
                    selectedNode={selectedNode}
                    handleInfoIconClick={handleInfoIconClick}
                    thresholdModalRef={thresholdModalRef}
                    t={t}
                />
            }
        />
    );
};

export const DeviceDetails = (): JSX.Element => {
    const theme: any = useTheme();
    const dispatch = useAppDispatch();
    const [searchKey, setSearchKey] = React.useState<any>(undefined);
    const [infoModalData, setInfoModalData] = React.useState<{
        isOpen: boolean;
        title: string;
        description: string;
    }>({ isOpen: false, title: '', description: '' });
    const navigate = useNavigate();
    const { deviceId } = useParams();
    const thresholdModalRef = React.useRef<any>(null);
    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    const selectedNode = useTypedSelector((state) => state['common']['selectedNode']);
    const [, debouncedValue] = useDebounce(undefined, 300, searchKey);
    const { pointConversion } = useTransformData();

    const currentOrg = useTypedSelector(selectedOrg);
    const { t } = useTranslation();

    const { data: profileData } = useGetUserProfileQuery({}, { refetchOnMountOrArgChange: true }) as ApiEndpointType<
        ApiResponseType<UserProfile>
    >;

    const { data: deviceData, isLoading: isDeviceLoading } = useGetDeviceByIdQuery(deviceId);
    const [deviceDetails, { isLoading, updateDetails }] = useCustomDetailsApi(deviceId, debouncedValue);
    const { thresholdUpdateLoader, handleAllExpandCollapse, expandedCategories, setExpandedCategories } =
        useHandleDeviceDetails(deviceDetails);
    const mqttConnectionStatus = useTypedSelector((state) => state['common']['mqttConnectionStatus']);

    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);
    const canUpdateThreshold = hasPermission('device-details_edit-devices');
    const canManageThreshold = hasPermission('telemetry-thresholds_view-devices');
    const manageChannelCategories = hasPermission('manage-channel-categories_manage-channel-selections');
    const [featureflagValue, setFeatureflagValue] = React.useState({} as FeatureFlagData);
    const { isIconLibraryEnabled } = useIconLibraryFlag();
    const { data: featureFlagData }: any = useGetAllFeatureFlagQuery({});

    React.useEffect(() => {
        const featureFlag = featureFlagData?.data?.find(
            (x: FeatureFlagData) => x.featureFlagName.toLowerCase() === 'channelmanager'
        );
        if (featureFlag) {
            setFeatureflagValue(featureFlag);
        }
    }, [featureFlagData]);
    React.useEffect(() => {
        if (canSubscribeToDeviceTopic(mqttConnectionStatus, profileData, selectedNode))
            subscribeTopic(
                getDeviceTopic(profileData, currentOrg, selectedNode, deviceId),
                (message: any, topic: any) => updateDetails(message, topic)
            );
    }, [mqttConnectionStatus, selectedNode, profileData?.data]);

    const getIcon = (channel: any): JSX.Element => getChannelIconComponent(channel, isIconLibraryEnabled);

    React.useEffect(
        () => () => {
            unsubscribeTopic(getDeviceTopic(profileData, currentOrg, selectedNode, deviceId));
        },
        []
    );

    React.useEffect(() => {
        if (!isDeviceLoading) {
            dispatch(setAppHeaderTitle(deviceData?.data?.name));
            dispatch(setAppHeaderSubTitle(deviceData?.data?.groupPath));
        }
        return (): any => {
            dispatch(setAppHeaderTitle(''));
            dispatch(setAppHeaderSubTitle(''));
        };
    }, [deviceData]);

    const shouldShowInfoIcon = (value: any): boolean => value.length > 32;
    const canShowThresholdIcon = (channel: any): boolean =>
        canManageThreshold &&
        channel?.type?.includes?.('Trend') &&
        channel?.dataType !== 'BOOLEAN' &&
        channel?.dataType !== 'STRING';

    const handleInfoIconClick = (title: string, description: string): void => {
        if (!description) return;
        setInfoModalData({
            isOpen: true,
            title: title ?? t('COMMON:DETAILS'),
            description,
        });
    };

    const handleCloseInfoModal = (): void => {
        setInfoModalData((prev) => ({ ...prev, isOpen: false }));
    };

    const handleCopyToClipboard = (): void => {
        if (infoModalData?.description && navigator?.clipboard?.writeText) {
            void navigator.clipboard.writeText(infoModalData.description).then(() => {
                dispatch(
                    setMessageContent({
                        isOpen: true,
                        message: t('COMMON:COPY_SUCCESS'),
                        duration: 3000,
                    })
                );
            });
        }
    };

    const infoModalActions: ModalActions[] = [
        {
            key: 'copy',
            label: 'Copy To Clipboard',
            variant: 'outlined',
            handleClick: handleCopyToClipboard,
        },
        {
            key: 'close',
            label: 'Close',
            variant: 'contained',
            close: true,
            handleClick: handleCloseInfoModal,
        },
    ];

    const renderDetailAccordions = (item: string): JSX.Element => (
        <Accordion
            key={item}
            expanded={expandedCategories[item] ?? true}
            onChange={(): void => {
                setExpandedCategories((previous: any) => ({ ...previous, [item]: !previous[item] }));
            }}
            defaultExpanded
        >
            <AccordionSummary
                expandIcon={<ExpandMore color="primary" />}
                aria-controls="panel1bh-content"
                id="expand"
                className="border-bottom-1"
            >
                <Typography
                    variant="body2"
                    fontWeight={'600'}
                    fontSize={'14px'}
                    sx={{ color: theme.palette?.primary?.main }}
                >
                    {item ?? '--'}
                </Typography>
            </AccordionSummary>
            <AccordionDetails className="padding-0">
                <List disablePadding>
                    {deviceDetails?.data[item]?.length > 0 ? (
                        deviceDetails.data[item].map((channel: any) => (
                            <ChannelRow
                                key={channel?.channelId}
                                channel={channel}
                                getIcon={getIcon}
                                pointConversion={pointConversion}
                                shouldShowInfoIcon={shouldShowInfoIcon}
                                canShowThresholdIcon={canShowThresholdIcon}
                                canUpdateThreshold={canUpdateThreshold}
                                selectedNode={selectedNode}
                                handleInfoIconClick={handleInfoIconClick}
                                thresholdModalRef={thresholdModalRef}
                                t={t}
                            />
                        ))
                    ) : (
                        <ChannelRow
                            channel={deviceDetails?.data[item]}
                            getIcon={getIcon}
                            pointConversion={pointConversion}
                            shouldShowInfoIcon={shouldShowInfoIcon}
                            canShowThresholdIcon={canShowThresholdIcon}
                            canUpdateThreshold={canUpdateThreshold}
                            selectedNode={selectedNode}
                            handleInfoIconClick={handleInfoIconClick}
                            thresholdModalRef={thresholdModalRef}
                            t={t}
                        />
                    )}
                </List>
            </AccordionDetails>
        </Accordion>
    );

    const getAccordionContent = (): any => {
        if (isLoading || thresholdUpdateLoader) {
            return renderLoadingAccordion(theme);
        }

        const details = deviceDetails?.data;
        if (!Object.keys(details ?? {}).length) {
            return <CustomEmptyState icon title={<CustomTransComponent translationKey={'COMMON:NO_DETAILS'} />} />;
        }

        return Object.keys(details).map((item: any) => renderDetailAccordions(item));
    };

    const handleEditChannel = (): void => {
        navigate(`/${currentRealmName}/channelSelector/${deviceId}`);
    };

    return (
        <>
            <Container className="padding-y-36">
                <DeviceDetailsHeader
                    theme={theme}
                    searchKey={searchKey}
                    featureflagValue={featureflagValue}
                    expandedCategories={expandedCategories}
                    searchPlaceholder={t('COMMON:SEARCH_PLACEHOLDER')}
                    collapseLabel={<CustomTransComponent translationKey={'DASHBOARD_MANAGEMENT:COLLAPSE_ALL'} />}
                    expandLabel={<CustomTransComponent translationKey={'DASHBOARD_MANAGEMENT:EXPAND_ALL'} />}
                    onSearch={setSearchKey}
                    onToggleExpand={handleAllExpandCollapse}
                    onEditChannel={handleEditChannel}
                    manageChannelCategories={manageChannelCategories}
                />

                <Stack>{getAccordionContent()}</Stack>
            </Container>

            <ThresholdModal ref={thresholdModalRef} />
            <CustomModal
                type="small"
                header={infoModalData?.title}
                isOpen={infoModalData?.isOpen}
                actions={infoModalActions}
            >
                <Typography variant="body1" sx={{ maxHeight: '50vh', overflowY: 'auto', pr: 1 }}>
                    {infoModalData?.description}
                </Typography>
            </CustomModal>
        </>
    );
};
